import React, { useCallback, useMemo, useState } from "react";
import { getPositionAbbreviation } from "../../helpers/positionAbbreviation";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

// Media Query
import { useMediaQuery } from "react-responsive";
import { breakpoints } from "../../style/size";

// Style
import {
  TopTableNames,
  TableNameWrap,
  TableWrap,
  TableContent,
  Player,
  Position,
  Passes,
  ImagesWrap,
} from "../../style/SmrtSearch";

// Images
import starOn from "../../images/star_on.svg";
import starOff from "../../images/star_off.svg";
import compare from "../../images/ic_compare.svg";
import compareBlue from "../../images/compare_blue.svg";
import shadowTeamIconDark from "../../images/ic_formation.svg";
import shadowTeamIconLight from "../../images/formation_blue.svg";
import emptyState from "../../images/contactLogo.svg";

import { ReactComponent as SortAscIcon } from "../../images/icons/sortasc_icon.svg";
import { ReactComponent as SortDescIcon } from "../../images/icons/sort_desc.svg";
import { ReactComponent as SortDefaultIcon } from "../../images/icons/sort_default.svg";

// Components
import Empty from "../../components/empty/index";
import Filter from "../modal/Filter";
import Modal from "../../components/modal/index";
import Button from "../button";

// Shadow team
import {
  fetchShadowTeams,
  fetchShadowTeamById,
  addShadowTeamPlayer,
} from "../../store/shadowTeam/shadowTeamService";
import {
  buildShadowTeamSlots,
  MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
  resolveShadowTeamAssignmentsBySlot,
} from "../../pages/shadowTeam/utils/formation";

// Language
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../notification/useNotification";
import { extractFirstCharacters, getPlayerParamMarkers } from "../../helpers";
import { NotificationList } from "../notification/NotificationList";
import { Tooltip } from "react-tooltip";
import { useLexicon } from "../../hooks/useLexicon";
import { openVideoList } from "../../helpers/service";
import { $apiV1, $apiV2 } from "../../config/api";

const Compare = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    display: block;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Shadow team modal styled components ─────────────────────────────────────
const ShadowTeamModalShell = styled.div`width: min(100%, 520px); display: flex; flex-direction: column; gap: 14px;`;
const ShadowTeamModalTitle = styled.h2`margin: 0; color: ${({ theme }) => theme.white}; font-family: "SatoshiBold"; font-size: 24px; line-height: 1.2; text-align: left;`;
const ShadowTeamModalSubtitle = styled.p`margin: 0; color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiRegular"; font-size: 13px; line-height: 1.5; text-align: left;`;
const ShadowTeamModalBody = styled.div`display: flex; flex-direction: column; gap: 10px; max-height: min(56vh, 520px); overflow-y: auto; padding-right: 4px;`;
const ShadowTeamModalBack = styled.button`display: inline-flex; align-items: center; gap: 6px; padding: 0; border: none; background: transparent; color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiMedium"; font-size: 13px; cursor: pointer; &:hover { color: ${({ theme }) => theme.white}; }`;
const ShadowTeamModalStepTitle = styled.p`margin: 0; color: ${({ theme }) => theme.textSecondary}; font-family: "SatoshiBold"; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; text-align: left;`;
const ShadowTeamModalSection = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const ShadowTeamModalItemButton = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 12px; border: 1px solid ${({ theme }) => theme.borderColor}; border-radius: 10px;
  background: ${({ theme }) => theme.cardBgAlt}; color: ${({ theme }) => theme.white}; font-family: "SatoshiMedium"; text-align: left; cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.green}; background: ${({ theme }) => theme.accentBg}; }
  &:disabled { cursor: wait; opacity: 0.6; }
`;
const ShadowTeamModalItemText = styled.div`min-width: 0; display: flex; flex-direction: column; gap: 3px;`;
const ShadowTeamModalItemTitle = styled.span`color: ${({ theme }) => theme.white}; font-family: "SatoshiBold"; font-size: 13px; line-height: 1.3;`;
const ShadowTeamModalItemMeta = styled.span`color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiRegular"; font-size: 11px; line-height: 1.35;`;
const ShadowTeamModalEmptyState = styled.div`padding: 16px 14px; border: 1px dashed ${({ theme }) => theme.borderColor}; border-radius: 12px; color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiRegular"; font-size: 13px; line-height: 1.5; text-align: left;`;
const ShadowTeamSlotSection = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const ShadowTeamSlotSectionLabel = styled.span`color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiMedium"; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; text-align: left;`;
const ShadowTeamSlotGrid = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const ShadowTeamSlotButton = styled.button`
  min-width: 54px; padding: 7px 12px; border: 1px solid ${({ theme }) => theme.borderColor}; border-radius: 999px;
  background: ${({ theme }) => theme.cardBgAlt}; color: ${({ theme }) => theme.textSecondary}; font-family: "SatoshiMedium"; font-size: 12px; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.green}; color: ${({ theme }) => theme.green}; }
  &:disabled { cursor: not-allowed; opacity: 0.45; }
`;
const ShadowTeamLoadingText = styled.div`color: ${({ theme }) => theme.textMuted}; font-family: "SatoshiMedium"; font-size: 13px; padding: 8px 0;`;

const SHADOW_TEAM_BAND_LABELS = {
  gk: "Goalkeeper", defense: "Defense", defensiveMid: "Defensive Mid",
  midfield: "Midfield", attackingMid: "Attacking Mid", attack: "Attack",
};

const getShadowTeamSlotGroups = (formation) => {
  if (!Array.isArray(formation?.lines) || formation.lines.length === 0) return [];
  const slots = buildShadowTeamSlots(formation.lines);
  const byLine = {};
  slots.forEach((slot) => {
    const key = slot.lineIndex;
    if (!byLine[key]) byLine[key] = { lineIndex: key, band: slot.band, slots: [] };
    byLine[key].slots.push(slot);
  });
  return Object.values(byLine);
};

const normalizeSTErrorMessage = (error, fallback) => {
  const msg = error?.response?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
};

const ClickableStatsHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.blueish};
  font-family: "SatoshiMedium";
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`;

const ClickableStatsHintDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.green};
  box-shadow: 0 0 8px ${(props) => props.theme.green};
  flex-shrink: 0;
`;

const SmrtSearchTable = ({ table, info }) => {
  const l = useLexicon();
  const isMobile = useMediaQuery({ query: `(${breakpoints.mobile})` });
  const { t } = useTranslation();
  const [openFilter, setOpenFiler] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { notifications, addNotification, removeNotification } = useNotification();

  // ─── Favourites ────────────────────────────────────────────────────────────
  const [favoritePlayers, setFavoritePlayers] = useState({});
  const [favoritePendingPlayers, setFavoritePendingPlayers] = useState({});

  const handleToggleFavorite = useCallback(async (item) => {
    const playerId = item.id;
    const isFavorite = Boolean(favoritePlayers[playerId]);
    if (favoritePendingPlayers[playerId]) return;
    setFavoritePendingPlayers((prev) => ({ ...prev, [playerId]: true }));
    try {
      if (isFavorite) {
        await $apiV1.delete(`/users/favorites/players/${playerId}/`);
      } else {
        await $apiV1.post("/users/favorites/players/", { player_id: playerId });
      }
      setFavoritePlayers((prev) => ({ ...prev, [playerId]: !isFavorite }));
    } catch (error) {
      addNotification(t("smrtSearch.favoriteError") || "Error updating favourite", "error");
    } finally {
      setFavoritePendingPlayers((prev) => ({ ...prev, [playerId]: false }));
    }
  }, [favoritePlayers, favoritePendingPlayers, addNotification, t]);

  // ─── Shadow team ───────────────────────────────────────────────────────────
  const shadowTeams = useSelector((state) => state.shadowTeam.teams);
  const shadowTeamLoadingTeams = useSelector((state) => state.shadowTeam.loading.teams);
  const [shadowTeamModalPlayer, setShadowTeamModalPlayer] = useState(null);
  const [shadowTeamSelectedTeamId, setShadowTeamSelectedTeamId] = useState(null);
  const [shadowTeamLoadingTeamId, setShadowTeamLoadingTeamId] = useState(null);

  const shadowTeamModalOpen = Boolean(shadowTeamModalPlayer);
  const shadowTeamModalPlayerName = shadowTeamModalPlayer?.name || shadowTeamModalPlayer?.display_name || "";

  const selectedShadowTeam = useMemo(() => {
    const id = Number(shadowTeamSelectedTeamId);
    if (!Number.isFinite(id)) return null;
    return shadowTeams.find((team) => Number(team?.id) === id) || null;
  }, [shadowTeamSelectedTeamId, shadowTeams]);

  const selectedShadowTeamHasLoadedAssignments = Array.isArray(selectedShadowTeam?.assignments);

  const isShadowTeamDetailLoading =
    shadowTeamLoadingTeamId !== null &&
    Number(shadowTeamLoadingTeamId) === Number(shadowTeamSelectedTeamId);

  const selectedShadowTeamSlotPlayers = useMemo(() => {
    if (!selectedShadowTeamHasLoadedAssignments) return {};
    return resolveShadowTeamAssignmentsBySlot(
      selectedShadowTeam?.assignments || [],
      selectedShadowTeam?.formation?.lines || []
    );
  }, [selectedShadowTeam, selectedShadowTeamHasLoadedAssignments]);

  const isShadowTeamSlotFull = useCallback(
    (slot) => (selectedShadowTeamSlotPlayers?.[slot?.key] || []).length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
    [selectedShadowTeamSlotPlayers]
  );

  const handleCloseShadowTeamModal = useCallback(() => {
    setShadowTeamModalPlayer(null);
    setShadowTeamSelectedTeamId(null);
    setShadowTeamLoadingTeamId(null);
  }, []);

  const handleOpenShadowTeam = useCallback(async (player) => {
    setShadowTeamModalPlayer(player);
    setShadowTeamSelectedTeamId(null);
    setShadowTeamLoadingTeamId(null);
    if (shadowTeams.length > 0) return;
    try {
      await dispatch(fetchShadowTeams()).unwrap();
    } catch (error) {
      addNotification(normalizeSTErrorMessage(error, t("shadowTeam.loadError")), "error");
    }
  }, [addNotification, dispatch, shadowTeams.length, t]);

  const handleSelectShadowTeam = useCallback(async (team) => {
    const teamId = Number(team?.id);
    if (!Number.isFinite(teamId)) return;
    setShadowTeamSelectedTeamId(teamId);
    setShadowTeamLoadingTeamId(teamId);
    try {
      await dispatch(fetchShadowTeamById(teamId)).unwrap();
    } catch (error) {
      setShadowTeamSelectedTeamId(null);
      addNotification(normalizeSTErrorMessage(error, t("shadowTeam.loadError")), "error");
    } finally {
      setShadowTeamLoadingTeamId((v) => (v === teamId ? null : v));
    }
  }, [addNotification, dispatch, t]);

  const handleAddToShadowTeamSlot = useCallback(async (slot) => {
    if (!shadowTeamModalPlayer || !selectedShadowTeam || isShadowTeamSlotFull(slot)) return;
    try {
      await dispatch(addShadowTeamPlayer({
        shadowTeamId: selectedShadowTeam.id,
        playerId: Number(shadowTeamModalPlayer.id),
        positionGroup: slot.positionGroup,
        positionOrder: slot.positionOrder,
      })).unwrap();
      addNotification(
        t("smrtSearch.shadowTeamAddedMessage", {
          name: shadowTeamModalPlayerName,
          team: selectedShadowTeam.name,
          slot: slot.shortLabel,
        }),
        "success"
      );
      handleCloseShadowTeamModal();
    } catch (error) {
      addNotification(normalizeSTErrorMessage(error, t("smrtSearch.shadowTeamErrorMessage")), "error");
    }
  }, [addNotification, dispatch, handleCloseShadowTeamModal, isShadowTeamSlotFull, selectedShadowTeam, shadowTeamModalPlayer, shadowTeamModalPlayerName, t]);

  const [tableTypeSorting, setTableTypeSorting] = useState({ type: 'default', param: '' }); // type default | asc | desc param:number

  const tableNames = [
    {
      id: 1,
      name: `${l(319)}`,
    },
    {
      id: 2,
      name: `${l(11)}`,
    },
    {
      id: 11,
      name: l(325) || "Matches Played",
      parameter_id: "matches_played",
    },
    {
      id: 5,
      name: l(424),
      parameter_id: 2,
    },
    {
      id: 7,
      name: l(39),
      parameter_id: 201,
    },
    {
      id: 8,
      name: l(346),
      parameter_id: 1,
    },
    {
      id: 9,
      name: `${l(424)}/90'`,
      parameter_id: 'goals_p90',
    },
    {
      id: 10,
      name: `${l(39)}/90'`,
      parameter_id: 'assists_p90',
    },
    {
      id: 4,
      name: l(19),
      parameter_id: 35,
    },
    {
      id: 3,
      name: l(71),
      parameter_id: 21,
    },
    {
      id: 6,
      name: l(65),
      parameter_id: 23,
    },
  ];

  const isCanClickPlayerStat = (parameter) => {
    return !parameter
      || parameter?.count === 0
      || parameter?.isMatchesPlayed
      || parameter?.isComputed
      || parameter.parameter_name?.includes('%')
      || parameter.parameter_name?.includes('Mins Played')
  }

  const getComputedValue = (item, paramId) => {
    const minsPlayed = item.params?.find(p => p.parameter_id === 1);
    if (paramId === 'goals_p90') {
      const goals = item.params?.find(p => p.parameter_id === 2);
      return minsPlayed?.count > 0 ? (goals?.count || 0) / minsPlayed.count * 90 : 0;
    }
    if (paramId === 'assists_p90') {
      const assists = item.params?.find(p => p.parameter_id === 201);
      return minsPlayed?.count > 0 ? (assists?.count || 0) / minsPlayed.count * 90 : 0;
    }
    return null;
  };

  const handleClickPlayerParam = async (param, player) => {
    const { count, parameter_id: param_id, parameter_name } = param;
    const { name, surname, id: player_id } = player;

    try {
      const controller = new AbortController();
      addNotification(`Fetching clips. Please don't close or refresh the page till the download is completed.`, "info", controller);
      document.body.classList.add('loading-cursor');
      const data = await getPlayerParamMarkers(player_id, param_id, '', controller);
      if (data?.results?.length > 0) {
        const eventsWithVideo = data?.results?.filter(item => Boolean(item.video));
        if (eventsWithVideo?.length > 0) {
          const markers = data.results.map(item => item.id).join(',');
          openVideoList(markers, `${parameter_name} - ${name} ${surname}`)
        } else {
          addNotification(`Video not available.`, 'warning')
        }
      } else {
        addNotification(`There are 0 clips in the response from the server.`, 'warning')
      }
    } catch (e) {
      console.log('error get param markers', e);
      if (e.type === 'canceled') {
        addNotification(`The request to receive ${count} clips of the player has been canceled.`, 'warning')
        return
      }
      addNotification(`Error in receiving ${count} player clips.`, 'error')
    } finally {
      document.body.classList.remove('loading-cursor');
    }
  }

  const [successPopup, setSuccessPopup] = useState(false);

  const handleCompare = async (item) => {
    const playerId = parseInt(item.id, 10);

    try {
      await $apiV2.post(`/platform/compare/add`, {
        player_id: playerId,
      });

      setSuccessPopup(!successPopup);
    } catch (error) {
      console.error("Error adding player to compare list:", error);
    }
  };

  const modal = (
    <>
      {/* Success added to compare  */}
      <Modal isOpen={successPopup} setIsOpen={setSuccessPopup} closeIc>
        <h1>{l(1046)}</h1>
        <br />
        <Button
          title={l(1047)}
          size={"full"}
          onClick={() => navigate(`/compare`)}
        />
      </Modal>
    </>
  );

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-US").format(number);
  };

  const handleSortTable = (param) => {
    if (tableTypeSorting.type === 'asc' && param.parameter_id === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'desc', param: param.parameter_id })
    } else if (tableTypeSorting.type === 'desc' && param.parameter_id === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'default', param: param.parameter_id })
      console.log('set default sort')
    } else {
      setTableTypeSorting({ type: 'asc', param: param.parameter_id })
    }
  }

  const sortTable = (a, b) => {
    if (tableTypeSorting.param) {
      if (tableTypeSorting.param === "matches_played") {
        const valueA = Number(a?.matches_played) || 0;
        const valueB = Number(b?.matches_played) || 0;

        if (tableTypeSorting.type === "asc") return valueA - valueB;
        if (tableTypeSorting.type === "desc") return valueB - valueA;
        return 0;
      }

      if (tableTypeSorting.param === 'goals_p90' || tableTypeSorting.param === 'assists_p90') {
        const valA = getComputedValue(a, tableTypeSorting.param);
        const valB = getComputedValue(b, tableTypeSorting.param);
        if (tableTypeSorting.type === 'asc') return valA - valB;
        if (tableTypeSorting.type === 'desc') return valB - valA;
        return 0;
      }
      const paramA = a.params?.find(param => param.parameter_id === tableTypeSorting.param);
      const paramB = b.params?.find(param => param.parameter_id === tableTypeSorting.param);
      if (paramA && paramB) {
        if (tableTypeSorting.type === 'asc') {
          return paramA.count - paramB.count;
        }
        if (tableTypeSorting.type === 'desc') {
          return paramB.count - paramA.count;
        }
      }
      if (paramA) return -1;
      if (paramB) return 1;
    }
    return 0;
  }

  return (
    <>
      {info?.length > 0 ? (
        <>
          <ClickableStatsHint>
            <ClickableStatsHintDot />
            {t("smrtSearch.clickableStatsHint")}
          </ClickableStatsHint>
          {!isMobile && (
            <TopTableNames>
              {tableNames.map((item, index) => {
                return (
                  <TableNameWrap
                    key={item.id}
                    className="tableNameWrapTeamPlayer"
                    onClick={() => handleSortTable(item)}
                    data-tooltip-id={`param-name-${item.id}`}
                  >
                    <h5 >{index < 2 ? item.name : extractFirstCharacters(item.name)}</h5>
                    {
                      index < 2 ? null
                        : tableTypeSorting.type === 'asc' && tableTypeSorting.param === item.parameter_id ? (
                          <SortAscIcon />
                        ) : tableTypeSorting.type === 'desc' && tableTypeSorting.param === item.parameter_id ? (
                          <SortDescIcon />
                        ) : (
                          <SortDefaultIcon />
                        )
                    }
                    <Tooltip
                      id={`param-name-${item.id}`}
                      place="bottom"
                      content={item.name}
                    />
                  </TableNameWrap>
                );
              })}
            </TopTableNames>
          )}
          <TableWrap>
            {info?.sort(sortTable)?.map((item) => {
              const player = table?.find((tableItem) => tableItem.id === item.id);
              const minsPlayedParam = item?.params?.find(a => a.parameter_id === 1);
              const goalsParam = item?.params?.find(a => a.parameter_id === 2);
              const assistsParam = item?.params?.find(a => a.parameter_id === 201);
              const goalsP90Count = minsPlayedParam?.count > 0
                ? parseFloat(((goalsParam?.count || 0) / minsPlayedParam.count * 90).toFixed(2))
                : 0;
              const assistsP90Count = minsPlayedParam?.count > 0
                ? parseFloat(((assistsParam?.count || 0) / minsPlayedParam.count * 90).toFixed(2))
                : 0;
              const params = [
                {
                  count: Number(item?.matches_played) || 0,
                  parameter_id: "matches_played",
                  parameter_name: l(325) || "Matches Played",
                  isMatchesPlayed: true,
                },
                goalsParam, //goals
                assistsParam, //Assists
                minsPlayedParam, // mins played
                { count: goalsP90Count, parameter_id: 'goals_p90', parameter_name: "Goals/90'", isComputed: true }, // Goals/90
                { count: assistsP90Count, parameter_id: 'assists_p90', parameter_name: "Assists/90'", isComputed: true }, // Assists/90
                item?.params?.find(a => a.parameter_id === 35), // passes
                item?.params?.find(a => a.parameter_id === 21), // Yellow Cards
                item?.params?.find(a => a.parameter_id === 23), // Red Cards
              ]
              const isFavorite = Boolean(favoritePlayers[item.id]);
              const favoriteLabel = isFavorite
                ? t("compareWorkspace.removeFavorite")
                : t("compareWorkspace.addFavorite");
              return (
                <TableContent key={item.id} className="tableLabelWrapTeamPlayer">
                  <Player className="teamPlayerTab" style={{ color: "#fff" }}>
                    <ImagesWrap>
                      {/* Favourite */}
                      <ActionIconButton
                        type="button"
                        onClick={() => handleToggleFavorite(item)}
                        disabled={Boolean(favoritePendingPlayers[item.id])}
                        aria-label={favoriteLabel}
                        data-tooltip-id={`team-fav-${item.id}`}
                      >
                        <img src={isFavorite ? starOn : starOff} alt="" aria-hidden="true" draggable="false" />
                      </ActionIconButton>
                      <Tooltip id={`team-fav-${item.id}`} place="bottom" content={favoriteLabel} />

                      {/* Compare */}
                      <ActionIconButton
                        type="button"
                        onClick={() => handleCompare(item)}
                        aria-label={t("smrtSearch.comparePlayerAction")}
                        data-tooltip-id={`team-compare-${item.id}`}
                      >
                        <img
                          src={localStorage.getItem("theme") === "light" ? compareBlue : compare}
                          alt=""
                          aria-hidden="true"
                          draggable="false"
                        />
                      </ActionIconButton>
                      <Tooltip id={`team-compare-${item.id}`} place="bottom" content={t("smrtSearch.comparePlayerAction")} />

                      {/* Shadow team */}
                      <ActionIconButton
                        type="button"
                        onClick={() => handleOpenShadowTeam({
                          id: item.id,
                          name: player?.display_name || item?.display_name || `${item.name} ${item.surname}`,
                        })}
                        aria-label={t("smrtSearch.shadowTeamPlayerAction")}
                        data-tooltip-id={`team-shadow-${item.id}`}
                      >
                        <img
                          src={localStorage.getItem("theme") === "light" ? shadowTeamIconLight : shadowTeamIconDark}
                          alt=""
                          aria-hidden="true"
                          draggable="false"
                        />
                      </ActionIconButton>
                      <Tooltip id={`team-shadow-${item.id}`} place="bottom" content={t("smrtSearch.shadowTeamPlayerAction")} />

                      {/* Player photo */}
                      <img
                        style={{ width: "40px", height: "40px", cursor: "pointer" }}
                        src={player?.photo || emptyState}
                        alt={player?.display_name || item?.display_name || `${item.name} ${item.surname}`}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = emptyState;
                        }}
                        onClick={() => navigate(`/tournaments/player/${item.id}`)}
                        onContextMenu={() => window.open(`/tournaments/player/${item.id}`, "_blank")}
                      />
                    </ImagesWrap>
                    <h3
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/tournaments/player/${item.id}`)}
                      onContextMenu={() =>
                        window.open(`/tournaments/player/${item.id}`, "_blank")
                      }
                    >
                      {player?.display_name || item?.display_name || `${item.name} ${item.surname}`}
                    </h3>
                  </Player>

                  {!isMobile && (
                    <>
                      <Position className="playerTeamPage">
                        <h3>{getPositionAbbreviation(player?.position)}</h3>
                      </Position>

                      {
                        params?.map((param, index) => {
                          return (
                            <Passes
                              key={index}
                              style={
                                isCanClickPlayerStat(param)
                                  ? { cursor: "not-allowed" }
                                  : { cursor: "pointer" }
                              }
                              onClick={() =>
                                !isCanClickPlayerStat(param) &&
                                handleClickPlayerParam(param, item)
                              }
                              onContextMenu={(e) => {
                                e.preventDefault()
                                !isCanClickPlayerStat(param) &&
                                  handleClickPlayerParam(param, item)
                              }
                              }
                            >
                              <h3>
                                {" "}
                                {param.count
                                  ? formatNumber(param.count)
                                  : param.count}
                              </h3>
                            </Passes>
                          );
                        })
                      }
                    </>
                  )}
                </TableContent>
              );
            })}
          </TableWrap>
        </>
      ) : (
        <Empty
          title={l(314)}
          text={t("smrtSearch.emptySearchDesc")}
        />
      )}

      <Filter setOpenFiler={setOpenFiler} openFilter={openFilter} />

      {/* Shadow team modal */}
      <Modal isOpen={shadowTeamModalOpen} setIsOpen={handleCloseShadowTeamModal} closeIc unstyledTypography>
        <ShadowTeamModalShell>
          <ShadowTeamModalTitle>{t("smrtSearch.shadowTeamModalTitle")}</ShadowTeamModalTitle>
          <ShadowTeamModalSubtitle>
            {t("smrtSearch.shadowTeamModalSubtitle", { name: shadowTeamModalPlayerName })}
          </ShadowTeamModalSubtitle>
          <ShadowTeamModalBody>
            {!selectedShadowTeam ? (
              <>
                <ShadowTeamModalStepTitle>
                  {t("smrtSearch.shadowTeamSelectTeamStep")}
                </ShadowTeamModalStepTitle>
                {shadowTeamLoadingTeams ? (
                  <ShadowTeamLoadingText>{t("compareWorkspace.loading")}</ShadowTeamLoadingText>
                ) : shadowTeams.length > 0 ? (
                  <ShadowTeamModalSection>
                    {shadowTeams.map((team) => (
                      <ShadowTeamModalItemButton
                        key={team.id}
                        type="button"
                        onClick={() => handleSelectShadowTeam(team)}
                      >
                        <ShadowTeamModalItemText>
                          <ShadowTeamModalItemTitle>{team.name}</ShadowTeamModalItemTitle>
                          <ShadowTeamModalItemMeta>
                            {team.formationName || team.formation?.name || ""}
                          </ShadowTeamModalItemMeta>
                        </ShadowTeamModalItemText>
                        <span aria-hidden="true">›</span>
                      </ShadowTeamModalItemButton>
                    ))}
                  </ShadowTeamModalSection>
                ) : (
                  <ShadowTeamModalEmptyState>
                    {t("smrtSearch.shadowTeamNoTeamsMessage")}
                  </ShadowTeamModalEmptyState>
                )}
              </>
            ) : (
              <>
                <ShadowTeamModalBack
                  type="button"
                  onClick={() => { setShadowTeamSelectedTeamId(null); setShadowTeamLoadingTeamId(null); }}
                >
                  ‹ {t("smrtSearch.shadowTeamBack")}
                </ShadowTeamModalBack>
                <ShadowTeamModalSection>
                  <ShadowTeamModalStepTitle>
                    {t("smrtSearch.shadowTeamSelectPositionStep")}
                  </ShadowTeamModalStepTitle>
                  <ShadowTeamModalItemMeta>
                    {selectedShadowTeam.name}
                    {selectedShadowTeam.formationName || selectedShadowTeam.formation?.name
                      ? ` · ${selectedShadowTeam.formationName || selectedShadowTeam.formation?.name}`
                      : ""}
                  </ShadowTeamModalItemMeta>
                </ShadowTeamModalSection>
                {isShadowTeamDetailLoading ? (
                  <ShadowTeamLoadingText>{t("compareWorkspace.loading")}</ShadowTeamLoadingText>
                ) : null}
                {getShadowTeamSlotGroups(selectedShadowTeam.formation).map((group) => (
                  <ShadowTeamSlotSection key={group.lineIndex}>
                    <ShadowTeamSlotSectionLabel>
                      {SHADOW_TEAM_BAND_LABELS[group.band] || group.band}
                    </ShadowTeamSlotSectionLabel>
                    <ShadowTeamSlotGrid>
                      {group.slots.map((slot) => (
                        <ShadowTeamSlotButton
                          key={slot.key}
                          type="button"
                          disabled={isShadowTeamSlotFull(slot)}
                          onClick={() => handleAddToShadowTeamSlot(slot)}
                        >
                          {slot.shortLabel}
                        </ShadowTeamSlotButton>
                      ))}
                    </ShadowTeamSlotGrid>
                  </ShadowTeamSlotSection>
                ))}
              </>
            )}
          </ShadowTeamModalBody>
        </ShadowTeamModalShell>
      </Modal>

      {/* display notifications */}
      <NotificationList
        notifications={notifications}
        onClose={removeNotification}
      />

      {modal}
    </>
  );
};

export default SmrtSearchTable;
